import type { TransferContextData } from '@ovComponents/4-module/workflowCompletion/workflowTypes';
import { isBusinessDayAndAllowWeekend } from '@utils/businessDays';
import dayjs, { type ConfigType } from 'dayjs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  checkTransferDelay,
  type DepositScheduleOptions,
  isValidTransferDelayCheck,
  resolveScheduledDateError,
  resolveScheduledDateErrorMessage,
  shouldClearScheduledDate,
  type TransferDelayCheck,
} from './depositSchedule';

type UseDepositScheduledDateChecksProp = {
  scheduledDate?: TransferContextData['scheduledDate'];
  country: string;
  options?: DepositScheduleOptions | null;
  isEft: boolean;
  isScheduled: boolean;
};

export const useDepositScheduledDateChecks = ({
  scheduledDate,
  country,
  options,
  isEft,
  isScheduled,
}: UseDepositScheduledDateChecksProp) => {
  const { t } = useTranslation('workflowCompletions');
  const delayDays = options?.transferDelay?.number;
  const [transferDelayCheck, setTransferDelayCheck] =
    useState<TransferDelayCheck | null>(null);
  const [scheduledDateIsBusinessDay, setScheduledDateIsBusinessDay] = useState<
    boolean | null
  >(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [delayResult, isBd] = await Promise.all([
        checkTransferDelay({ scheduledDate, days: delayDays, country }),
        isBusinessDayAndAllowWeekend({
          date: dayjs(scheduledDate).utc().toDate(),
          country,
        }),
      ]);
      if (!cancelled) {
        setTransferDelayCheck(delayResult);
        setScheduledDateIsBusinessDay(isBd);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [scheduledDate, delayDays, country]);

  const validTransferDelayCheck = isValidTransferDelayCheck({
    isEft,
    isScheduled,
    options,
    transferDelayCheck,
    scheduledDateIsBusinessDay,
  });

  const scheduledDateError = resolveScheduledDateError({
    transferDate: scheduledDate,
    options,
    transferDelayCheck,
    scheduledDateIsBusinessDay,
  });

  const { key, params } = resolveScheduledDateErrorMessage({
    options,
    transferDelayCheck,
  });

  const shouldClearNewDate = async (date: ConfigType) =>
    shouldClearScheduledDate({
      date,
      options,
      newDelayCheck: await checkTransferDelay({
        scheduledDate: dayjs(date).toDate(),
        days: delayDays,
        country,
      }),
    });

  return {
    transferDelayCheck,
    validTransferDelayCheck,
    scheduledDateError,
    scheduledDateErrorMessage: t(key, params),
    shouldClearNewDate,
  };
};
